import React, { useState } from "react";
import { SearchOutlined } from "@ant-design/icons";
import PhoneNumberHeader from "./PhoneNumberHeader";
import AssignExtensionList from "./AssignExtensionList";
import CustomTypography from "../../../commonComponents/CustomTypography";
import DebounceInput from "../../../commonComponents/DebounceInput";
import useGoto from "../../../../hooks/useGoto";
import CustomSkeleton from "../../../commonComponents/CustomSkeleton";
import { getItemIdentifier } from "./gotoHelpers";
import useLang from "../../../../hooks/useLang";

const AssignExtensionDrawer = ({
  phoneData,
  extensionData,
  userData,
  deviceData,
  locationId,
  onExtensionSelect,
}) => {
  const [gotoLang] = useLang("GoTo");
  const [searchText, setSearchText] = useState("");

  // Fetch extensions based on locationId and search
  const { data: extensionsData, loading } = useGoto({
    operation: "getCallExtensions",
    variables: {
      locationId: locationId ? parseInt(locationId, 10) : null,
      search: searchText || undefined,
      isActive: true, // Only show active extensions
      page: 1,
      pageSize: 100, // Get a large list for selection
    },
    skip: !locationId,
    fetchPolicy: "network-only",
  });

  // Get current extension ID from any of the 4 types
  const currentExtensionId =
    phoneData?.callExtension?.id ||
    extensionData?.id ||
    userData?.callExtension?.id ||
    deviceData?.callExtension?.id;

  // Get the item identifier (number, name, etc.) for the header using helper function
  const itemIdentifier = getItemIdentifier(phoneData, extensionData, userData, deviceData);

  const extensions = extensionsData?.data || [];

  return (
    <div>
      {/* Header showing current assignment */}
      <div className="mb-6">
        <PhoneNumberHeader
          phoneNumber={itemIdentifier}
          assignedTo={currentExtensionId ? `Extension ${phoneData?.callExtension?.number}` : null}
          assignedToRole={null}
          label={gotoLang.currentlyAssignedTo}
        />
      </div>

      <div className="my-6">
        <CustomTypography variant="text" strong>
          {gotoLang.selectExtensionToAssign || "Select extension to assign"}
        </CustomTypography>

        <DebounceInput
          className="mt-5"
          placeholder={gotoLang.searchExtension || "Search extension by number"}
          value={searchText}
          onChange={setSearchText}
          suffix={<SearchOutlined className="icon-color" />}
          allowClear
        />
      </div>

      {/* Extensions List - Scrollable with fixed height */}
      <div className="overflow-y-auto pr-2" style={{ height: "calc(100vh - 376px)" }}>
        {loading ? (
          <CustomSkeleton
            skeletonCount={5}
            skeletonWidth="34rem"
            skeletonHeight="3rem"
            skeletonMarginBottom="15px"
          />
        ) : (
          <AssignExtensionList
            extensions={extensions}
            onExtensionSelect={onExtensionSelect}
            initialSelectedExtensionId={currentExtensionId}
          />
        )}
      </div>
    </div>
  );
};

export default AssignExtensionDrawer;
